import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  KanbanSquare,
  Users,
  StickyNote,
  Timer,
  Bot,
  Settings as SettingsIcon,
  LogOut,
  Terminal,
} from "lucide-react";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/projects", label: "Projects", icon: KanbanSquare, testid: "nav-projects" },
  { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" },
  { to: "/app/notes", label: "Notes", icon: StickyNote, testid: "nav-notes" },
  { to: "/app/time", label: "Time", icon: Timer, testid: "nav-time" },
  { to: "/app/ai", label: "AI Suite", icon: Bot, testid: "nav-ai" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-black text-neutral-100 felt">
      <aside
        className="w-64 shrink-0 border-r border-neutral-800 bg-black/80 backdrop-blur-xl flex flex-col"
        data-testid="app-sidebar"
      >
        <Link to="/app" className="px-6 py-6 border-b border-neutral-900 flex items-center gap-3" data-testid="brand-link">
          <div className="w-9 h-9 border border-red-600/70 flex items-center justify-center bg-black">
            <Terminal size={16} className="text-red-500" />
          </div>
          <div>
            <div className="font-serif text-xl tracking-tight leading-none">AceHack</div>
            <div className="label-mono text-[10px] mt-1">// suite v1.0</div>
          </div>
        </Link>

        <nav className="flex-1 py-4">
          {navItems.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-red-500 bg-red-500/5 text-red-400"
                    : "border-transparent text-neutral-400 hover:text-neutral-100 hover:bg-neutral-900/40"
                }`
              }
            >
              <Icon size={15} />
              <span className="font-jet uppercase tracking-[0.18em] text-[11px]">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-neutral-900 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 border border-neutral-700 bg-neutral-900 flex items-center justify-center text-xs font-jet text-amber-400">
              {user?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-jet truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-[10px] text-neutral-500 uppercase tracking-[0.18em]">
                {user?.role || "owner"}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full btn-ghost flex items-center justify-center gap-2"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
